import classNames from 'classnames';
import type { ReactNode } from 'react';
import { createElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CampaignClass, IObjectiveAvailability } from '@feathr/blackbox';
import { ECampaignObjective, getObjectiveAvailabilityForCampaignClass } from '@feathr/blackbox';
import { getIconForAction } from '@feathr/hooks';
import { useAccount } from '@feathr/report_components';

export interface IObjectiveOptionBase {
  id: ECampaignObjective;
  name: ReactNode;
  description: ReactNode;
  disabled: boolean;
}

export interface IObjectiveOptionWithIcon extends Omit<IObjectiveOptionBase, 'disabled'> {
  icon: ReactNode;
  disabled?: boolean;
}

export interface IObjectiveOptionWithPrefix extends IObjectiveOptionBase {
  prefix: ReactNode;
}

interface IStyleClasses {
  name: string;
  lockIcon: string;
  prefix: string;
  prefixDisabled: string;
}

interface IObjectiveUtilsReturn {
  objectiveText: Record<ECampaignObjective, { name: string; description: string }>;
  createSelectOptions: (availability: IObjectiveAvailability) => IObjectiveOptionWithIcon[];
  createRadioOptions: (
    availability: IObjectiveAvailability,
    styleClasses: IStyleClasses,
  ) => IObjectiveOptionWithPrefix[];
  createOptionsForCampaignType: (
    campaignType: CampaignClass,
    styleClasses: IStyleClasses,
  ) => IObjectiveOptionWithPrefix[];
}

/**
 * Hook that provides objective utilities with consistent text content
 */
export function useObjectiveUtils(): IObjectiveUtilsReturn {
  const { t } = useTranslation();
  const { isPlatformLite } = useAccount();

  /**
   * Single source of truth for objective text content
   */
  const objectiveText = useMemo(
    () => ({
      [ECampaignObjective.Reach]: {
        name: t('Build Reach and Awareness'),
        description: t(
          'Prioritizes breadth of audience reach, brand exposure, and awareness building.',
        ),
      },
      [ECampaignObjective.HighDemand]: {
        name: t('Deepen Engagement'),
        description: t('Focuses on depth of audience engagement and optimizes for clicks.'),
      },
      [ECampaignObjective.Conversions]: {
        name: t('Drive Conversions'),
        description: t(
          'Targets the people ad placements, and creatives that are most likely to convert for your goal(s).',
        ),
      },
      [ECampaignObjective.Custom]: {
        name: t('Custom Objective'),
        description: t('Choose your own campaign setup and optimization preferences.'),
      },
    }),
    [t],
  );

  /**
   * Creates objective options for use in select dropdowns (ObjectiveSelect)
   */
  const createSelectOptions = useMemo(
    () =>
      (availability: IObjectiveAvailability): IObjectiveOptionWithIcon[] => [
        {
          id: ECampaignObjective.Reach,
          name: objectiveText[ECampaignObjective.Reach].name,
          description: objectiveText[ECampaignObjective.Reach].description,
          icon: getIconForAction('reach'),
          disabled: !availability.reach,
        },
        {
          id: ECampaignObjective.HighDemand,
          name: objectiveText[ECampaignObjective.HighDemand].name,
          description: objectiveText[ECampaignObjective.HighDemand].description,
          icon: getIconForAction('deepen'),
          disabled: !availability.high_demand,
        },
        {
          id: ECampaignObjective.Conversions,
          name: objectiveText[ECampaignObjective.Conversions].name,
          description: objectiveText[ECampaignObjective.Conversions].description,
          icon: getIconForAction('drive'),
          disabled: !availability.conversions,
        },
        {
          id: ECampaignObjective.Custom,
          name: objectiveText[ECampaignObjective.Custom].name,
          description: objectiveText[ECampaignObjective.Custom].description,
          icon: getIconForAction('customize'),
          disabled: !availability.custom,
        },
      ],
    [objectiveText],
  );

  /**
   * Creates objective options for use in radio groups (ObjectiveStep)
   */
  const createRadioOptions = useMemo(
    () =>
      (
        availability: IObjectiveAvailability,
        styleClasses: IStyleClasses,
      ): IObjectiveOptionWithPrefix[] => {
        return [
          {
            id: ECampaignObjective.Reach,
            description: objectiveText[ECampaignObjective.Reach].description,
            name: createElement(
              'div',
              { className: styleClasses.name },
              objectiveText[ECampaignObjective.Reach].name,
              isPlatformLite &&
                createElement(
                  'span',
                  { className: styleClasses.lockIcon },
                  getIconForAction('locked'),
                ),
            ),
            prefix: createElement(
              'div',
              {
                className: classNames(styleClasses.prefix, {
                  [styleClasses.prefixDisabled]: !availability.reach,
                }),
              },
              getIconForAction('reach'),
            ),
            disabled: !availability.reach,
          },
          {
            id: ECampaignObjective.HighDemand,
            description: objectiveText[ECampaignObjective.HighDemand].description,
            name: createElement(
              'div',
              { className: styleClasses.name },
              objectiveText[ECampaignObjective.HighDemand].name,
            ),
            prefix: createElement(
              'div',
              {
                className: classNames(styleClasses.prefix, {
                  [styleClasses.prefixDisabled]: !availability.high_demand,
                }),
              },
              getIconForAction('deepen'),
            ),
            disabled: !availability.high_demand,
          },
          {
            id: ECampaignObjective.Conversions,
            description: objectiveText[ECampaignObjective.Conversions].description,
            name: createElement(
              'div',
              { className: styleClasses.name },
              objectiveText[ECampaignObjective.Conversions].name,
            ),
            prefix: createElement(
              'div',
              {
                className: classNames(styleClasses.prefix, {
                  [styleClasses.prefixDisabled]: !availability.conversions,
                }),
              },
              getIconForAction('drive'),
            ),
            disabled: !availability.conversions,
          },
          {
            id: ECampaignObjective.Custom,
            description: objectiveText[ECampaignObjective.Custom].description,
            name: createElement(
              'div',
              { className: styleClasses.name },
              objectiveText[ECampaignObjective.Custom].name,
            ),
            prefix: createElement(
              'div',
              {
                className: classNames(styleClasses.prefix, {
                  [styleClasses.prefixDisabled]: !availability.custom,
                }),
              },
              getIconForAction('customize'),
            ),
            disabled: !availability.custom,
          },
        ];
      },
    [objectiveText, isPlatformLite],
  );

  /**
   * Gets objective availability and creates radio options for a campaign type
   * Convenience function for ObjectiveStep
   */
  const createOptionsForCampaignType = useMemo(
    () =>
      (campaignType: CampaignClass, styleClasses: IStyleClasses): IObjectiveOptionWithPrefix[] => {
        const availability = getObjectiveAvailabilityForCampaignClass(campaignType, {
          isPlatformLite,
        });
        return createRadioOptions(availability, styleClasses);
      },
    [createRadioOptions, isPlatformLite],
  );

  return {
    objectiveText,
    createSelectOptions,
    createRadioOptions,
    createOptionsForCampaignType,
  };
}
